import { format } from "date-fns";
import { localeOptions } from "internationalization/i18n";
import { getAppointmentAssignees } from "./templateTagResolvers";
import { TemplateTagType } from "./templateTags";

// Format a date value with the app wide date format of the locale
const formatDateValue = (value: unknown, locale: string): string => {
    // Convert the value to a date object
    const dateObject = new Date(String(value));

    // Use the date format which the app uses for the locale
    const dateFormat = localeOptions[locale as keyof typeof localeOptions]?.dateFormat;

    // Format with the app date format or fallback to the locale default
    if (dateFormat) {
        return format(dateObject, dateFormat);
    }
    return new Intl.DateTimeFormat(locale).format(dateObject);
};

export const formatTemplateValue = (
    value: unknown,
    type: TemplateTagType,
    options: { locale?: string; currency?: string; multilineSeparator?: string } = {}
): string => {
    const locale = options.locale ?? 'nl-NL';
    const currency = options.currency ?? 'EUR';
    const multilineSeparator = options.multilineSeparator ?? '\n';
    let formattedValue = '';

    if (value != null && value !== '') {
        if (type === 'date') {
            formattedValue = formatDateValue(value, locale);
        } else if (type === 'time') {
            formattedValue = new Intl.DateTimeFormat(locale, {
                hour: '2-digit',
                minute: '2-digit'
            }).format(new Date(String(value)));
        } else if (type === 'datetime') {
            formattedValue = `${formatDateValue(value, locale)} ${new Intl.DateTimeFormat(locale, {
                hour: '2-digit',
                minute: '2-digit'
            }).format(new Date(String(value)))}`;
        } else if (type === 'price') {
            formattedValue = new Intl.NumberFormat(locale, {
                style: 'currency',
                currency
            }).format(Number(value));
        } else if (type === 'duration') {
            formattedValue = `${Number(value).toLocaleString(locale)} uur`;
        } else if (type === 'multiline' && Array.isArray(value)) {
            formattedValue = value.map(item => formatAppointmentLine(item, locale)).join(multilineSeparator);
        } else {
            formattedValue = String(value);
        }
    }

    return formattedValue;
};

const formatAppointmentLine = (appointment: any, locale: string): string => {
    const startDate = appointment?.start_datetime ? new Date(appointment.start_datetime) : null;
    const endDate = appointment?.end_datetime ? new Date(appointment.end_datetime) : null;

    const date = startDate
        ? formatDateValue(appointment.start_datetime, locale)
        : '';

    const startTime = startDate
        ? new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(startDate)
        : '';

    const endTime = endDate
        ? new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(endDate)
        : '';

    const assignees = getAppointmentAssignees(appointment);
    const time = endTime ? `${startTime} - ${endTime}` : startTime;

    return [date, time, assignees].filter(Boolean).join(' · ');
};
